import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

type Option = {
  value: string;
  label: string;
};

type SelectFieldProps = {
  name: string;
  label: string;
  value: string;
  options: Option[];
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLSelectElement>) => void;
  error?: boolean;
  disabled?: boolean;
  className?: string;
  hidelabel?: boolean;
};

const SelectField = ({
  name,
  label,
  value,
  options,
  onChange,
  onBlur,
  error = false,
  disabled = false,
  className,
  hidelabel,
}: SelectFieldProps) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);
  const selectedOption = options.find((opt) => opt.value === value) || {
    value: '',
    label: '',
  };
  const hasValue = Boolean(value);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (val: string) => {
    // mimic change event
    const syntheticEvent = {
      target: { name, value: val },
      currentTarget: { name, value: val },
    } as unknown as React.ChangeEvent<HTMLSelectElement>;

    onChange(syntheticEvent);
    onBlur({
      ...syntheticEvent,
      type: 'blur',
    } as React.FocusEvent<HTMLSelectElement>);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!open) {

      if (['ArrowDown', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => (prev <= 0 ? options.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex >= 0) handleSelect(options[highlightIndex].value);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
    else if (e.key === 'Tab') {
    e.preventDefault();
  }
  };
  const dropdownClassName = className ? className : 'max-h-60';

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        onKeyDown={handleKeyDown}
        className={`!focus:outline-black relative h-[47px] w-full cursor-pointer rounded-md border pl-3 pr-5 text-left text-[16px] ${

          error ? 'border-red-500 focus:border-red-500' : 'border-gray-400 focus:border-gray-500'

        } ${disabled ? 'cursor-not-allowed !text-gray-900' : 'bg-white !text-gray-900'}`}
      >
        <span className={`block truncate ${!hasValue ? 'text-gray-400' : 'text-gray-900'}`}>{selectedOption?.label || ' '}</span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </span>
        {!(hidelabel && hasValue) && (
          <label
            className={`absolute left-3 bg-white px-1 text-16 font-normal transition-all duration-200 ${
              (hasValue || open) && !hidelabel ? '-top-2.5 translate-y-0' : 'top-1/2 -translate-y-1/2'
            } ${error ? 'text-red-500' : 'text-gray-500'}`}
          >
            {label}
          </label>
        )}
      </button>

      {open && (
        <ul
          className={`absolute z-40 mt-1 w-full overflow-auto rounded-md bg-white py-1 text-16 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm ${dropdownClassName}`}
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#9CA3AF #F3F4F6',
          }}
        >
          {options.map((option, idx) => {
            const isHighlighted = idx === highlightIndex;
            return (
              <li
                key={option.value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(option.value)}
                className={`relative cursor-pointer select-none py-2 pl-3 pr-4 text-gray-900 hover:bg-gray-200 ${
                  isHighlighted ? 'bg-gray-300' : ''
                }`}
              >
                <span className={`block truncate ${option.value === value ? 'font-medium' : 'font-normal'}`}>
                  {option.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SelectField;
